/**
 * NavigationBehavior.tsx
 *
 * Entry pages, exit pages, most/least visited pages (one reusable ranked-bar
 * list, since they share the same {path, value} shape), and page flow (its
 * own list — sequence data, not a ranked count) for a caller-supplied date
 * range. Fetching goes through useAPI()'s requestEntryPages /
 * requestExitPages / requestPagePopularity / requestPageFlow — currently
 * placeholders in useAPI.ts, so these lists will show their error state
 * until those requests are implemented for real. Styling (.split-bar-block,
 * .split-bar-heading, .status-row, .nav-list-*, .page-flow-*, etc.) is
 * defined once in the parent AnalyticsDashboard's <style> block.
 */
import { useEffect, useState } from 'react';
import useAPI from '../../Hooks/useAPI';

// ---------------------------------------------------------------------
// Shared generic data-fetch hook — avoids repeating the same
// loading/error/cancelled boilerplate for every endpoint below.
// ---------------------------------------------------------------------
function useAsyncData<T>(fetcher: () => Promise<{ data: T }>, deps: React.DependencyList) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetcher()
            .then((response) => {
                if (!cancelled) setData(response.data);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { data, loading, error };
}

// ---------------------------------------------------------------------
// Ranked bar list — reused for Entry pages, Exit pages, Most/Least visited
// ---------------------------------------------------------------------
interface RankedRow {
    path: string;
    value: number;
}

// Sorts client-side rather than trusting the API's row order, so "top" (index
// 0) is always correct for the list's own direction — e.g. Least visited
// shows the actual lowest count first, not whatever order the backend sent.
function toRankedRows<K extends string>(
    rows: Record<K, unknown>[] | null,
    path: 'path',
    valueKey: K,
    sortDirection: 'desc' | 'asc' = 'desc'
): RankedRow[] {
    if (!rows) return [];
    const mapped = rows.map((r) => ({ path: String(r[path as unknown as K]), value: Number(r[valueKey]) || 0 }));
    return mapped.sort((a, b) => (sortDirection === 'desc' ? b.value - a.value : a.value - b.value));
}

function RankedPageRow({ row, max, valueLabel }: { row: RankedRow; max: number; valueLabel: string }) {
    return (
        <div className="nav-list-row" title={`${row.path}: ${row.value.toLocaleString()} ${valueLabel}`}>
            <div className="nav-list-path">{row.path}</div>
            <div className="nav-list-bar-track">
                <div className="nav-list-bar-fill" style={{ width: `${(row.value / max) * 100}%` }} />
            </div>
            <div className="nav-list-value">{row.value.toLocaleString()}</div>
        </div>
    );
}

function RankedTopRow({ row, valueLabel }: { row: RankedRow; valueLabel: string }) {
    return (
        <div className="nav-list-top-row" title={`${row.path}: ${row.value.toLocaleString()} ${valueLabel}`}>
            <div className="nav-list-path">{row.path}</div>
            <div className="nav-list-top-value">{row.value.toLocaleString()}</div>
        </div>
    );
}

function RankedPageList({
    title,
    rows,
    valueLabel,
    loading,
    error,
}: {
    title: string;
    rows: RankedRow[];
    valueLabel: string;
    loading: boolean;
    error: string | null;
}) {
    const max = Math.max(...rows.map((r) => r.value), 1);
    const [topRow] = rows;

    return (
        <div className="split-bar-block nav-list-block">
            <div className="split-bar-heading">{title}</div>
            {loading && <div className="status-row">Loading…</div>}
            {error && <div className="status-row status-row--error">{error}</div>}
            {!loading && !error && (
                <div className="nav-list-rows">
                    {topRow && <RankedTopRow row={topRow} valueLabel={valueLabel} />}

                    {rows.length > 1 && (
                        <details className="nav-list-more-toggle">
                            <summary>Show {rows.length - 1} more</summary>
                            <div className="nav-list-more-rows">
                                {rows.map((r) => (
                                    <RankedPageRow key={r.path} row={r} max={max} valueLabel={valueLabel} />
                                ))}
                            </div>
                        </details>
                    )}

                    {rows.length === 0 && <div className="status-row">No data for this range.</div>}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Page flow — sequence of pages per session (not a ranked count, so it
// gets its own list rather than reusing RankedPageList)
// ---------------------------------------------------------------------
interface PageFlowRawRow {
    sessionId: string;
    sequenceNumber: number;
    path: string;
    viewedAt: string;
}

interface PageFlowSession {
    sessionId: string;
    pages: string[];
    startedAt: string;
}

function groupPageFlowBySession(rows: PageFlowRawRow[]): PageFlowSession[] {
    const bySession = new Map<string, PageFlowRawRow[]>();
    rows.forEach((r) => {
        if (!bySession.has(r.sessionId)) bySession.set(r.sessionId, []);
        bySession.get(r.sessionId)!.push(r);
    });

    const sessions: PageFlowSession[] = Array.from(bySession.entries()).map(([sessionId, pageviews]) => {
        const sorted = [...pageviews].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        return {
            sessionId,
            pages: sorted.map((p) => p.path),
            startedAt: sorted[0]?.viewedAt ?? '',
        };
    });

    sessions.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
    return sessions;
}

function PageFlowList({ rows, loading, error }: { rows: PageFlowRawRow[]; loading: boolean; error: string | null }) {
    const sessions = groupPageFlowBySession(rows);

    return (
        <div className="split-bar-block nav-list-block">
            <div className="split-bar-heading">Page flow (recent sessions)</div>
            {loading && <div className="status-row">Loading…</div>}
            {error && <div className="status-row status-row--error">{error}</div>}
            {!loading && !error && (
                <div className="page-flow-rows">
                    {sessions.map((s) => (
                        <div key={s.sessionId} className="page-flow-row">
                            <div className="page-flow-meta">
                                {s.startedAt ? new Date(s.startedAt).toLocaleString() : '—'} · session {s.sessionId.slice(0, 8)}
                            </div>
                            <div className="page-flow-path">
                                {s.pages.map((p, i) => (
                                    <span key={i} className="page-flow-step">
                                        {i > 0 && <span className="page-flow-arrow">→</span>}
                                        {p}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                    {sessions.length === 0 && <div className="status-row">No sessions for this range.</div>}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------
export default function NavigationBehaviorSection({ start, end }: { start: Date; end: Date }) {
    const { requestEntryPages, requestExitPages, requestPagePopularity, requestPageFlow } = useAPI();

    const entryPages = useAsyncData<{ path: string; sessionCount: string }[]>(
        () => requestEntryPages(start.toISOString(), end.toISOString()),
        [start.getTime(), end.getTime(), requestEntryPages]
    );
    const exitPages = useAsyncData<{ path: string; sessionCount: string }[]>(
        () => requestExitPages(start.toISOString(), end.toISOString()),
        [start.getTime(), end.getTime(), requestExitPages]
    );
    const mostVisited = useAsyncData<{ path: string; views: string }[]>(
        () => requestPagePopularity(start.toISOString(), end.toISOString(), 'most'),
        [start.getTime(), end.getTime(), requestPagePopularity]
    );
    const leastVisited = useAsyncData<{ path: string; views: string }[]>(
        () => requestPagePopularity(start.toISOString(), end.toISOString(), 'least'),
        [start.getTime(), end.getTime(), requestPagePopularity]
    );
    const pageFlow = useAsyncData<PageFlowRawRow[]>(
        () => requestPageFlow(start.toISOString(), end.toISOString()),
        [start.getTime(), end.getTime(), requestPageFlow]
    );

    return (
        <div className="nav-behavior-section">
            <div className="nav-behavior-heading">Navigation &amp; behavior</div>

            <div className="nav-list-pair-row">
                <RankedPageList
                    title="Entry pages"
                    rows={toRankedRows(entryPages.data, 'path', 'sessionCount')}
                    valueLabel="sessions"
                    loading={entryPages.loading}
                    error={entryPages.error}
                />

                <RankedPageList
                    title="Exit pages"
                    rows={toRankedRows(exitPages.data, 'path', 'sessionCount')}
                    valueLabel="sessions"
                    loading={exitPages.loading}
                    error={exitPages.error}
                />
            </div>

            <div className="nav-list-pair-row">
                <RankedPageList
                    title="Most visited pages"
                    rows={toRankedRows(mostVisited.data, 'path', 'views')}
                    valueLabel="views"
                    loading={mostVisited.loading}
                    error={mostVisited.error}
                />

                <RankedPageList
                    title="Least visited pages"
                    rows={toRankedRows(leastVisited.data, 'path', 'views', 'asc')}
                    valueLabel="views"
                    loading={leastVisited.loading}
                    error={leastVisited.error}
                />
            </div>

            <PageFlowList rows={pageFlow.data ?? []} loading={pageFlow.loading} error={pageFlow.error} />
        </div>
    );
}
